import React, { useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
  IonBackButton, IonButton, IonIcon, IonList, IonSpinner, IonAlert,
  IonFab, IonFabButton,
} from '@ionic/react';
import { pencil, trash, add } from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useBinder, useDeleteBinder, useRemoveCardFromBinder, useAddCardsToBinder } from '../hooks/useBinders';
import { useCards } from '../hooks/useCards';
import CardItem from '../components/CardItem';

const BinderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const binderId = Number(id);
  const { data: binder, isLoading } = useBinder(binderId);
  const deleteBinder = useDeleteBinder();
  const removeCard = useRemoveCardFromBinder();
  const addCards = useAddCardsToBinder();
  const { data: allCardsData } = useCards({ per_page: 100 });
  const history = useHistory();
  const [showDelete, setShowDelete] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<number[]>([]);

  const handleDelete = async () => {
    await deleteBinder.mutateAsync(binderId);
    history.replace('/binders');
  };

  const handleRemoveCard = async (cardId: number) => {
    await removeCard.mutateAsync({ binderId, cardId });
  };

  const handleAddCards = async () => {
    if (selectedCardIds.length > 0) {
      await addCards.mutateAsync({ binderId, cardIds: selectedCardIds });
      setSelectedCardIds([]);
      setShowAddModal(false);
    }
  };

  const binderCardIds = new Set(binder?.cards?.map((c) => c.id) || []);
  const availableCards = allCardsData?.items.filter((c) => !binderCardIds.has(c.id)) || [];

  if (isLoading || !binder) {
    return (
      <IonPage>
        <IonHeader><IonToolbar color="primary"><IonButtons slot="start"><IonBackButton defaultHref="/binders" /></IonButtons><IonTitle>Binder</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding ion-text-center"><IonSpinner /></IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start"><IonBackButton defaultHref="/binders" /></IonButtons>
          <IonTitle>{binder.name}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push(`/binders/${id}/edit`)}>
              <IonIcon icon={pencil} />
            </IonButton>
            <IonButton onClick={() => setShowDelete(true)} color="danger">
              <IonIcon icon={trash} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16, borderBottom: `3px solid ${binder.color}` }}>
          {binder.description && <p>{binder.description}</p>}
          <p><strong>{binder.card_count}</strong> cards | Value: <strong>${binder.total_value.toFixed(2)}</strong></p>
        </div>

        <IonList>
          {binder.cards && binder.cards.length > 0 ? (
            binder.cards.map((card) => (
              <CardItem key={card.id} card={card} onClick={() => history.push(`/cards/${card.id}`)} />
            ))
          ) : (
            <div className="ion-text-center ion-padding">No cards in this binder. Tap + to add some!</div>
          )}
        </IonList>

        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => setShowAddModal(true)}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        {showAddModal && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setShowAddModal(false)}
          >
            <div
              style={{
                background: 'var(--ion-background-color, #fff)', borderRadius: 12,
                width: '90%', maxWidth: 450, maxHeight: '80vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--ion-color-light)' }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>Add Cards</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--ion-color-primary)' }}
                >Cancel</button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1, padding: '8px 20px' }}>
                {availableCards.length > 0 ? (
                  availableCards.map((card) => (
                    <label
                      key={card.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                        borderBottom: '1px solid var(--ion-color-light)', cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCardIds.includes(card.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCardIds((prev) => [...prev, card.id]);
                          } else {
                            setSelectedCardIds((prev) => prev.filter((cid) => cid !== card.id));
                          }
                        }}
                        style={{ width: 20, height: 20, flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600 }}>{card.name}</div>
                        <div style={{ fontSize: 13, color: 'var(--ion-color-medium)' }}>
                          {card.game?.name}{card.set_name && ` - ${card.set_name}`}
                        </div>
                      </div>
                    </label>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: 20 }}>No more cards available to add.</div>
                )}
              </div>
              {selectedCardIds.length > 0 && (
                <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ion-color-light)' }}>
                  <IonButton expand="block" onClick={handleAddCards}>
                    Add {selectedCardIds.length} Card{selectedCardIds.length > 1 ? 's' : ''}
                  </IonButton>
                </div>
              )}
            </div>
          </div>
        )}

        <IonAlert
          isOpen={showDelete}
          onDidDismiss={() => setShowDelete(false)}
          header="Delete Binder"
          message={`Delete "${binder.name}"? Cards won't be deleted.`}
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            { text: 'Delete', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default BinderDetail;
